// 1-2-3-4-5 stepper across the top of the wizard.
// Allows jumps backward and to any step <= maxReached. Forward jumps past
// `maxReached` are blocked — the user must complete the previous step first.
// A11y: <ol> + aria-current="step" on the active step.

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { WIZARD_TOTAL_STEPS } from '../../constants';
import { media } from '../../../../utils/media';

const List = styled.ol`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 16px 24px;
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  gap: 0;
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  background: none;
  border: 0;
  font: inherit;
  color: inherit;
  padding: 0;
`;

const Bubble = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  background: ${({ $state, theme }) => {
    if ($state === 'active') return theme.colors.PRIMARY;
    if ($state === 'done') return theme.colors.SUCCESS_GREEN;
    return theme.colors.NEU_100;
  }};
  color: ${({ $state, theme }) => {
    if ($state === 'pending') return theme.colors.NEU_600;
    return theme.colors.NEU_0;
  }};
`;

const Label = styled.span`
  font-size: 14px;
  font-weight: ${({ $state }) => ($state === 'active' ? 600 : 500)};
  color: ${({ $state, theme }) => ($state === 'pending' ? theme.colors.NEU_600 : theme.colors.NEU_PRIMARY)};

  ${media.lessThan('tablet')} {
    display: none;
  }
`;

const Line = styled.span`
  flex: 1;
  height: 2px;
  margin: 0 16px;
  background: ${({ $done, theme }) => ($done ? theme.colors.SUCCESS_GREEN : theme.colors.NEU_100)};
  min-width: 24px;
`;

const STEPS = [
  { i: 1, key: 'basics' },
  { i: 2, key: 'audience' },
  { i: 3, key: 'content' },
  { i: 4, key: 'schedule' },
  { i: 5, key: 'review' },
];

const STEP_LABELS = {
  basics: ['Basics'],
  audience: ['Audience'],
  content: ['Content'],
  schedule: ['Schedule'],
  review: ['Review'],
};

const stateFor = (step, current, maxReached) => {
  if (step === current) return 'active';
  if (step < current || step <= maxReached) return 'done';
  return 'pending';
};

const WizardStepper = ({ step, maxReached, onJump }) => {
  const { t } = useTranslation();
  const total = WIZARD_TOTAL_STEPS;

  return (
    <List>
      {STEPS.map((s, idx) => {
        const state = stateFor(s.i, step, maxReached);
        const clickable = s.i <= maxReached && s.i !== step;
        const handleClick = () => clickable && onJump?.(s.i);
        return (
          <React.Fragment key={s.key}>
            <Item
              $clickable={clickable}
              onClick={handleClick}
              aria-current={state === 'active' ? 'step' : undefined}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : -1}
            >
              <Bubble $state={state}>{s.i}</Bubble>
              <Label $state={state}>{t(`campaigns.step.${s.key}`, STEP_LABELS[s.key][0])}</Label>
            </Item>
            {idx < total - 1 && <Line $done={s.i < step || s.i < maxReached} />}
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default WizardStepper;
